import * as React from 'react'
import moment from 'moment'
import { observer, inject } from 'mobx-react'
import { message } from 'antd'
import { CurrentCompanyStore } from '../../../../../../stores/CurrentCompanyStore'
import { parseErrorMsg } from '../../../../../../utilities/helpers'
import { LoadingAnimation } from '../../../../../../components/LoadingAnimation'
import { FormError } from '../../../../../../components/FormError'
import { QuickBooksApi } from '@getgreenline/quickbooks'
import { JournalEntriesTable } from './JournalEntriesTable'
import { Button } from 'antd-v4'
import { DateRangePicker } from 'react-dates'

export interface ISyncedJournalEntryResponse {
  journalEntryId: string
  date: string
}

interface IState {
  isLoading: boolean
  isSyncing: boolean
  startDate: string | null
  endDate: string | null
  appliedStartDate: string
  appliedEndDate: string
  syncedJournalEntries: ISyncedJournalEntryResponse[]
  errorMessage: string
  focusedInput: 'startDate' | 'endDate' | null
}

interface IProps {
  currentCompanyStore?: CurrentCompanyStore
}

const monthFormat = 'YYYY-MM'
@inject('currentCompanyStore')
@observer
export class QuickBooksSync extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props)
    this.state = {
      isLoading: false,
      isSyncing: false,
      startDate: moment().startOf('month').format('YYYY-MM-DD'),
      endDate: moment().endOf('month').format('YYYY-MM-DD'),
      appliedStartDate: moment().startOf('month').format('YYYY-MM-DD'),
      appliedEndDate: moment().endOf('month').format('YYYY-MM-DD'),
      focusedInput: null,
      syncedJournalEntries: [],
      errorMessage: '',
    }
  }

  componentDidMount() {
    this.getSyncDates()
  }

  getSyncDates = async () => {
    const { startDate, endDate } = this.state
    if (!startDate || !endDate) {
      return this.setState({ errorMessage: 'Please select start date and end date' })
    }

    this.setState({ isLoading: true, errorMessage: '' })

    try {
      const syncedJournalEntriesResponse = await QuickBooksApi.getSyncedQuickBooksJournalEntries(
        this.props.currentCompanyStore!.company!.id,
        startDate,
        endDate,
      )

      await this.setState({
        syncedJournalEntries: syncedJournalEntriesResponse.syncedJournalEntries,
        appliedStartDate: startDate,
        appliedEndDate: endDate,
      })
    } catch (error) {
      message.error(parseErrorMsg(error) || 'There was an error getting the synced dates')
    } finally {
      this.setState({ isLoading: false })
    }
  }

  onDatesChange = (params: { startDate: moment.Moment | null; endDate: moment.Moment | null }) => {
    this.setState({
      startDate: params.startDate ? moment(params.startDate).format('YYYY-MM-DD') : null,
      endDate: params.endDate ? moment(params.endDate).format('YYYY-MM-DD') : null,
    })
  }

  sync = async (date: string) => {
    try {
      this.setState({ isSyncing: true })
      await QuickBooksApi.syncQuickBooksJournalEntries(
        this.props.currentCompanyStore!.company!.id,
        date,
      )
      await this.getSyncDates()
    } catch (error) {
      message.error(parseErrorMsg(error) || `There was an error while syncing for ${date}`)
    } finally {
      this.setState({ isSyncing: false })
    }
  }

  delete = async (date: string) => {
    try {
      this.setState({ isSyncing: true })
      await QuickBooksApi.removeQuickBooksJournalEntry(
        this.props.currentCompanyStore!.company!.id,
        date,
      )
      await this.getSyncDates()
    } catch (error) {
      message.error(parseErrorMsg(error) || `There was an error while deleting for ${date}`)
    } finally {
      this.setState({ isSyncing: false })
    }
  }

  render() {
    const {
      isLoading,
      startDate,
      endDate,
      syncedJournalEntries,
      errorMessage,
      focusedInput,
      appliedStartDate,
      appliedEndDate,
      isSyncing,
    } = this.state

    return (
      <div style={{ maxWidth: '30em' }}>
        <div>
          <div className='d-flex flex-row'>
            <DateRangePicker
              startDateId='qbo-sync-date-range-start-date'
              endDateId='qbo-sync-date-range-end-date'
              showClearDates={false}
              isOutsideRange={(day: moment.Moment) => false}
              startDate={startDate ? moment(startDate) : null}
              endDate={endDate ? moment(endDate) : null}
              minimumNights={0}
              onDatesChange={this.onDatesChange}
              block={true}
              focusedInput={focusedInput}
              onFocusChange={(focused: 'startDate' | 'endDate' | null) =>
                this.setState({ focusedInput: focused })
              }
            />

            <Button className='ml-3' onClick={this.getSyncDates}>
              Apply
            </Button>
          </div>
          <div className='mt-3' style={{ minWidth: '40em' }}>
            <FormError message={errorMessage} />
          </div>
        </div>

        {isLoading ? (
          <div>
            <LoadingAnimation />
          </div>
        ) : (
          <JournalEntriesTable
            appliedEndDate={appliedEndDate}
            appliedStartDate={appliedStartDate}
            syncedJournalEntries={syncedJournalEntries}
            sync={this.sync}
            remove={this.delete}
            isSyncing={isSyncing}
          />
        )}
      </div>
    )
  }
}
